/* Homepage: download native Dawn interactions only when that feature is used.
 * Not a global click/scroll unlock. Shopify services and analytics stay intact. */
(() => {
  'use strict';
  if (window.NSHomeProductAssets) return;
  const config = document.currentScript?.dataset || {};
  const pending = new Map();
  const actions = new WeakMap();
  const replaying = new WeakSet();
  const elements = {
    searchForm: 'search-form', predictiveSearch: 'predictive-search',
    cartItems: 'cart-items', cartDrawer: 'cart-drawer',
    cartNotification: 'cart-notification', quantityPopover: 'quantity-popover',
    productForm: 'product-form', quickAdd: 'quick-add-modal',
    detailsDisclosure: 'details-disclosure', detailsModal: 'details-modal'
  };
  function load(name) {
    if (customElements.get(elements[name])) return Promise.resolve();
    if (pending.has(name)) return pending.get(name);
    const url = config[name + 'Url'];
    if (!url) return Promise.reject(new Error('Missing interaction asset: ' + name));
    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = false;
      let finished = false;
      let timedOut = false;
      const timer = setTimeout(() => {
        if (finished) return;
        timedOut = true;
        // Removing an in-flight script does not reliably cancel execution.
        // Keep this request cached until its real load/error to avoid a late
        // script and a retry both defining the same native Dawn classes.
        reject(new Error('Interaction asset timed out: ' + name));
      }, 15000);
      function finish(ok) {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        script.onload = script.onerror = null;
        if (ok && customElements.get(elements[name])) {
          if (timedOut) pending.delete(name);
          resolve();
        }
        else {
          pending.delete(name);
          script.remove();
          reject(new Error('Interaction asset unavailable: ' + name));
        }
      }
      script.onload = () => finish(true);
      script.onerror = () => finish(false);
      document.head.append(script);
    });
    pending.set(name, promise);
    return promise;
  }
  async function cart() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      await load('cartItems');
      await load('cartDrawer');
      if (typeof drawer.open !== 'function') throw new Error('Cart drawer unavailable');
      // Quantity tooltip is an enhancement; a failed tooltip must not block cart.
      if (drawer.querySelector('quantity-popover')) load('quantityPopover').catch(() => {});
    } else {
      const notification = document.querySelector('cart-notification');
      if (notification) {
        await load('cartNotification');
        if (typeof notification.renderContents !== 'function') throw new Error('Cart notification unavailable');
      }
    }
  }
  async function product() {
    await cart();
    await load('productForm');
  }
  let searchPending;
  function search() {
    if (searchPending) return searchPending;
    const inputs = [...document.querySelectorAll('search-form input[type="search"],predictive-search input[type="search"]')];
    inputs.forEach(input => input.closest('search-form,predictive-search').setAttribute('aria-busy', 'true'));
    searchPending = load('searchForm')
      .then(() => config.predictiveSearchUrl && document.querySelector('predictive-search') ? load('predictiveSearch') : undefined)
      .then(() => {
        // Upgrade listeners missed keystrokes while loading. Resume only the
        // current, focused query; do not fetch hidden/offscreen search inputs.
        const input = document.activeElement;
        if (input?.matches('search-form input[type="search"],predictive-search input[type="search"]')) {
          const host = input.closest('search-form,predictive-search');
          if (input.value && typeof host.onChange === 'function') host.onChange();
        }
      }).catch(error => {
        searchPending = undefined;
        // The normal GET /search form is the intentional failure fallback.
        console.warn('[Nailspiracy] Predictive search unavailable; normal search remains available.');
      }).finally(() => inputs.forEach(input => input.closest('search-form,predictive-search')?.removeAttribute('aria-busy')));
    return searchPending;
  }
  function nativeSubmit(form) {
    form.querySelector('input[name="id"]')?.removeAttribute('disabled');
    HTMLFormElement.prototype.submit.call(form);
  }
  function act(event, target, prepare, perform, fallback) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (actions.has(target)) return;
    target.setAttribute('aria-busy', 'true');
    const promise = Promise.resolve().then(prepare).then(() => {
      if (!target.isConnected) return;
      // Do not turn successful user actions into retries/fallback purchases.
      // Failure handling below applies to preparation, never an AJAX purchase.
      return true;
    }, () => false).then(ready => {
      if (!target.isConnected) return;
      target.removeAttribute('aria-busy');
      actions.delete(target);
      if (!ready) { fallback(); return; }
      replaying.add(target);
      try { perform(); } finally { replaying.delete(target); }
    }).finally(() => {
      target.removeAttribute('aria-busy');
      actions.delete(target);
    });
    // No unhandled rejection if a third-party listener throws. Never retry a
    // purchase after execution begins: that could create a second cart entry.
    actions.set(target, promise);
    promise.catch(() => console.warn('[Nailspiracy] Interaction failed after initialization; no automatic purchase retry.'));
  }
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form.matches('product-form form') || replaying.has(form)) return;
    if (!actions.has(form) && customElements.get('product-form') && typeof form.closest('product-form')?.cart?.getSectionsToRender === 'function') return;
    if (!actions.has(form) && customElements.get('product-form') && !document.querySelector('cart-drawer,cart-notification')) return;
    const submitter = event.submitter;
    act(event, form, product, () => {
      const host = form.closest('product-form');
      if (typeof host.onSubmitHandler !== 'function') { nativeSubmit(form); return; }
      form.requestSubmit(submitter?.isConnected && submitter.form === form ? submitter : undefined);
    }, () => nativeSubmit(form));
  }, true);
  function plainClick(event) {
    return !event.defaultPrevented && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && (!('button' in event) || event.button === 0);
  }
  document.addEventListener('click', event => {
    if (!plainClick(event)) return;
    const button = event.target.closest('button');
    const opener = button?.closest('modal-opener');
    let modal;
    try { modal = opener && document.querySelector(opener.dataset.modal); } catch (_) {}
    if (modal?.matches('quick-add-modal') && !replaying.has(button)) {
      if (!actions.has(button) && customElements.get('quick-add-modal') && customElements.get('product-form')) return;
      const url = button.dataset.productUrl;
      act(event, button, async () => { await product(); await load('quickAdd'); }, () => button.click(), () => {
        if (url) window.location.assign(url);
      });
      return;
    }
    const link = event.target.closest('#cart-icon-bubble');
    const drawer = document.querySelector('cart-drawer');
    if (link && drawer && !replaying.has(link) && (actions.has(link) || !customElements.get('cart-drawer'))) {
      act(event, link, cart, () => drawer.open(link), () => window.location.assign(link.href || config.cartUrl));
      return;
    }
    const detailsModal = event.target.closest('details-modal');
    if (detailsModal && !customElements.get('details-modal') && !replaying.has(event.target)) {
      const target = event.target.closest('summary,button');
      if (target) act(event, target, () => load('detailsModal'), () => target.click(), () => {
        const details = detailsModal.querySelector('details');
        if (details) details.open = !details.open;
      });
    }
  }, true);
  document.addEventListener('keydown', event => {
    if (event.key !== ' ' || !event.target.closest('#cart-icon-bubble') || customElements.get('cart-drawer') || !document.querySelector('cart-drawer')) return;
    const link = event.target.closest('#cart-icon-bubble');
    act(event, link, cart, () => document.querySelector('cart-drawer').open(link), () => window.location.assign(link.href || config.cartUrl));
  }, true);
  document.addEventListener('focusin', event => {
    if (event.target.matches('search-form input[type="search"],predictive-search input[type="search"]')) search();
    if (event.target.closest('details-modal') && !customElements.get('details-modal')) load('detailsModal').catch(() => {});
    if (event.target.closest('details-disclosure,header-menu') && !customElements.get('details-disclosure')) load('detailsDisclosure').catch(() => {});
  }, true);
  document.addEventListener('click', event => {
    if (event.target.closest('details-disclosure,header-menu') && !customElements.get('details-disclosure')) load('detailsDisclosure').catch(() => {});
  }, true);
  // API retained for section integrations; calling init is deliberately inert.
  window.NSHomeProductAssets = Object.assign(() => {}, {search, cart, product});
})();
